import React, { useState, useEffect, useCallback } from 'react';
import { ModernButton } from '../ui/ModernButton';
import { dbService } from '../../services/DatabaseService';
import { ArrowLeftIcon, SparklesIcon } from '../ui/Icons';
import { HiLanguage } from 'react-icons/hi2';
import { DEFAULT_POINT_SCORING_PROMPT, POINT_SCORING_PROMPT_PLACEHOLDERS, describeGeminiApiError } from '../../utils/aiPrompts';
import { DEFAULT_RECOMMENDATION_TIERS, normalizeRecommendationTiers } from '../../utils/scoreConfig';
import { geminiFetch, getActiveGeminiModel, setActiveGeminiModel, SUPPORTED_GEMINI_MODELS } from '../../services/geminiConfig';
import { UserColorSettings } from '../settings/UserColorSettings';
import { GeminiAttemptLog } from '../settings/GeminiAttemptLog';

// --- Einstellungsseite ---
export function SettingsPage({ navigateTo }) {
    const [questionnairePrompt, setQuestionnairePrompt] = useState("");
    const [qualificationPrompt, setQualificationPrompt] = useState("");
    const [defaultQualificationPrompt, setDefaultQualificationPrompt] = useState("");
    const [analysisPrompt, setAnalysisPrompt] = useState("");
    const [defaultAnalysisPrompt, setDefaultAnalysisPrompt] = useState("");
    const [evaluationLanguageInstruction, setEvaluationLanguageInstruction] = useState("");
    const [scoringPrompt, setScoringPrompt] = useState("");
    const [pointScoringPrompt, setPointScoringPrompt] = useState("");
    // Kept as raw input values while editing; normalized (sorted, clamped,
    // lowest tier forced to 0) on save.
    const [recommendationTiers, setRecommendationTiers] = useState(DEFAULT_RECOMMENDATION_TIERS.map((tier) => ({ ...tier })));
    const [selectedJobTitle, setSelectedJobTitle] = useState("");
    const [templates, setTemplates] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [notification, setNotification] = useState(null);
    const [activeTab, setActiveTab] = useState("evaluation"); // "evaluation", "questionnaire", "qualification", "scoring", "language", "model"
    const [geminiModel, setGeminiModel] = useState(() => getActiveGeminiModel());

    const showNotification = (message, type = 'success') => {
        setNotification({ message, type });
        setTimeout(() => setNotification(null), 3000);
    };

    const hideNotification = () => {
        setNotification(null);
    };

    // Load settings and templates on component mount
    const loadSettings = useCallback(async () => {
        try {
            const [settings, templatesData] = await Promise.all([
                dbService.getSettings(),
                dbService.getTemplates()
            ]);
            
            setQuestionnairePrompt(settings.questionnairePrompt);
            setQualificationPrompt(settings.qualificationPrompt || "");
            setDefaultQualificationPrompt(settings.qualificationPrompt || "");
            setAnalysisPrompt(settings.systemPrompt || "");
            setDefaultAnalysisPrompt(settings.systemPrompt || "");
            setEvaluationLanguageInstruction(settings.evaluationLanguageInstruction || "");
            setPointScoringPrompt(settings.pointScoringPrompt || "");
            setRecommendationTiers(normalizeRecommendationTiers(settings.recommendationTiers));
            setTemplates(templatesData);
            
            // Set default job title if available
            if (templatesData.length > 0 && !selectedJobTitle) {
                setSelectedJobTitle(templatesData[0].title);
            }
        } catch (error) {
            console.error('Error loading settings:', error);
            // Use default values on error
            setQuestionnairePrompt("Du erstellst hochspezifische Fragebögen für Positionen in Heidelberg, Deutschland (Altstadt). Fokussiere auf die SPEZIELLE BRANCHE/DOMÄNE, nicht nur allgemeine Fähigkeiten. Erstelle Fragen zu: Branchenspezifisches Fachwissen, Produktkenntnisse, lokale Marktkenntnis, Fachbegriffe und Konzepte, branchenspezifische Erfahrungen. Stelle sicher, dass die Fragen klar, relevant und praxisorientiert sind. Berücksichtige, dass Fachkräfte Branchenterminologie als selbstverständlich betrachten. Erstelle eine ausgewogene Mischung aus 2-3 Textfragen, 1-2 Einzelauswahlfragen und 1-2 Mehrfachauswahlfragen. MINDESTENS 1 kreative Persönlichkeits-Textfrage einbauen. Wichtige Informationen in den Fragen selbst integrieren, nicht in Beschreibungen. Geschäftsbegriffe als selbstverständlich voraussetzen. Beschreibungen nur bei Bedarf und kurz halten.");
            setQualificationPrompt("Du bist ein HR-Experte für Einzelhandel. Bewerte die Bewerbungsantworten auf einer Skala von 0-10 Punkten für die Eignung als Einzelhandelsfachkraft.\n\nBewerte jede Frage einzeln und gib dann eine Gesamtbewertung ab.\n\nBewertungskriterien:\n- default_1 (Abschluss): 0-2 Punkte (Hauptschule=0, Realschule=1, Abitur+=2)\n- default_2 (Erfahrung): 0-2 Punkte (0 Jahre=0, 1-2 Jahre=1, 3+ Jahre=2)\n- default_3 (Position): 0-1 Punkt (spezifische Position=1, unklar=0)\n- default_4 (Starttermin): 0-1 Punkt (schneller Start=1, unklar=0)\n- default_5 (Schichtarbeit): 0-2 Punkte (Ja=2, Teilweise=1, Nein=0)\n- default_6 (Nebentätigkeit): 0-1 Punkt (Nein=1, Ja=0)\n- default_7 (Nebentätigkeit Details): 0-1 Punkt (wenn relevant, detailliert=1, unklar=0)\n- default_8 (Ausfälle): 0-2 Punkte (0-1x=2, 2-3x=1, 4-6x=0, >6x=0)\n- default_9 (Anfahrtsweg): 0-1 Punkt (kurz/angemessen=1, sehr weit=0)\n- default_10 (Verbleib): 0-1 Punkt (langfristig=1, kurzfristig=0)\n- default_11 (Termine): 0-1 Punkt (keine/wenig=1, viele=0)");
        }
    }, [selectedJobTitle]);

    useEffect(() => {
        loadSettings();
    }, [loadSettings]);

    // Load qualification prompt when job title changes
    useEffect(() => {
        const loadQualificationPrompt = async () => {
            if (!selectedJobTitle || !templates.length) return;
            
            try {
                const template = templates.find(t => t.title === selectedJobTitle);
                if (template && template.qualification_prompt) {
                    setQualificationPrompt(template.qualification_prompt);
                } else {
                    // Use default prompt if no template-specific prompt exists
                    setQualificationPrompt("Du bist ein HR-Experte. Bewerte die Bewerbungsantworten auf einer Skala von 0-10 Punkten für die Eignung.\n\nBewerte jede Frage einzeln und gib dann eine Gesamtbewertung ab.\n\nBewertungskriterien:\n- default_1 (Abschluss): 0-2 Punkte (Hauptschule=0, Realschule=1, Abitur+=2)\n- default_2 (Erfahrung): 0-2 Punkte (0 Jahre=0, 1-2 Jahre=1, 3+ Jahre=2)\n- default_3 (Position): 0-1 Punkt (spezifische Position=1, unklar=0)\n- default_4 (Starttermin): 0-1 Punkt (schneller Start=1, unklar=0)\n- default_5 (Schichtarbeit): 0-2 Punkte (Ja=2, Teilweise=1, Nein=0)\n- default_6 (Nebentätigkeit): 0-1 Punkt (Nein=1, Ja=0)\n- default_7 (Nebentätigkeit Details): 0-1 Punkt (wenn relevant, detailliert=1, unklar=0)\n- default_8 (Ausfälle): 0-2 Punkte (0-1x=2, 2-3x=1, 4-6x=0, >6x=0)\n- default_9 (Anfahrtsweg): 0-1 Punkt (kurz/angemessen=1, sehr weit=0)\n- default_10 (Verbleib): 0-1 Punkt (langfristig=1, kurzfristig=0)\n- default_11 (Termine): 0-1 Punkt (keine/wenig=1, viele=0)");
                }
            } catch (error) {
                console.error('Error loading qualification prompt:', error);
            }
        };
        
        loadQualificationPrompt();
    }, [selectedJobTitle, templates]);

    // Load scoring prompt when job title changes
    useEffect(() => {
        if (!selectedJobTitle || !templates.length) return;
        const template = templates.find(t => t.title === selectedJobTitle);
        if (template && template.scoring_prompt !== undefined) {
            setScoringPrompt(template.scoring_prompt || '');
        } else {
            setScoringPrompt('');
        }
    }, [selectedJobTitle, templates]);

    // Load analysis prompt when job title changes
    useEffect(() => {
        const loadAnalysisPrompt = async () => {
            if (!selectedJobTitle || !templates.length) return;
            
            try {
                const template = templates.find(t => t.title === selectedJobTitle);
                console.log('Loading analysis prompt for:', selectedJobTitle, 'Template found:', !!template);
                if (template) {
                    console.log('Template analysis_prompt:', template.analysis_prompt);
                    // Always use the template's analysis_prompt, even if it's empty
                    setAnalysisPrompt(template.analysis_prompt || "");
                } else {
                    console.log('No template found, using default prompt');
                    // Use default prompt if no template found
                    setAnalysisPrompt("Du bewertest hochspezifische Bewerbungen für Positionen in Heidelberg, Deutschland (Altstadt). Fokussiere auf die SPEZIELLE BRANCHE/DOMÄNE, nicht nur allgemeine Fähigkeiten. Analysiere: Branchenspezifisches Fachwissen, Produktkenntnisse, lokale Marktkenntnis, Fachbegriffe und Konzepte, branchenspezifische Erfahrungen. Bewerte Klarheit, Relevanz und Problemlösungskompetenz. Berücksichtige, dass Fachkräfte Branchenterminologie als selbstverständlich betrachten.");
                }
            } catch (error) {
                console.error('Error loading analysis prompt:', error);
            }
        };
        
        loadAnalysisPrompt();
    }, [selectedJobTitle, templates]);

    const handleSave = async () => {
        try {
            setIsLoading(true);
            
            // Save general settings to PocketBase
            await dbService.saveSettings({
                systemPrompt: defaultAnalysisPrompt,
                questionnairePrompt: questionnairePrompt,
                qualificationPrompt: defaultQualificationPrompt,
                pointScoringPrompt: pointScoringPrompt,
                evaluationLanguageInstruction: evaluationLanguageInstruction,
                recommendationTiers: normalizeRecommendationTiers(recommendationTiers)
                // Note: systemPrompt is now used as the default fallback prompt for analysis
            });
            // Reflect the normalization (clamping/sorting) back in the inputs
            setRecommendationTiers(normalizeRecommendationTiers(recommendationTiers));
            
            // Save qualification and analysis prompts to selected template
            if (selectedJobTitle) {
                const template = templates.find(t => t.title === selectedJobTitle);
                if (template) {
                    // Create updated template object with new prompts
                    const updatedTemplate = {
                        ...template,
                        qualification_prompt: qualificationPrompt,
                        analysis_prompt: analysisPrompt,
                        scoring_prompt: scoringPrompt
                    };
                    
                    console.log('Saving template with data:', {
                        id: template.id,
                        title: selectedJobTitle,
                        qualification_prompt: qualificationPrompt,
                        analysis_prompt: analysisPrompt
                    });
                    
                    await dbService.updateTemplate(template.id, updatedTemplate);
                    console.log(`Prompts saved for template: ${selectedJobTitle}`);
                }
            }
            
            console.log('Settings saved to PocketBase successfully');
            showNotification("Einstellungen gespeichert!");
        } catch (error) {
            console.error('Error saving settings:', error);
            setError('Fehler beim Speichern der Einstellungen');
        } finally {
            setIsLoading(false);
        }
    };

    const improveWithAI = async (type) => {
        setIsLoading(true); 
        setError(null);
        
        let currentPrompt = "";
        let setterFunction = null;
        let promptDescription = "";
        
        switch (type) {
            case 'analysis':
                currentPrompt = analysisPrompt;
                setterFunction = setAnalysisPrompt;
                promptDescription = "HR-Bewertungs-KI";
                break;
            case 'questionnaire':
                currentPrompt = questionnairePrompt;
                setterFunction = setQuestionnairePrompt;
                promptDescription = "Quiz-Erstellungs-KI";
                break;
            case 'qualification':
                currentPrompt = qualificationPrompt;
                setterFunction = setQualificationPrompt;
                promptDescription = "Qualifikationsbewertungs-KI";
                break;
            case 'scoring':
                currentPrompt = scoringPrompt;
                setterFunction = setScoringPrompt;
                promptDescription = "Punktebewertungs-KI";
                break;
            default:
                throw new Error("Unbekannter Prompt-Typ");
        }
        
        const userPrompt = `Du bist ein Experte für HR-System-Prompts. Formuliere aus den folgenden Stichpunkten einen klaren, prägnanten und professionellen System-Prompt für eine ${promptDescription}. Der Prompt soll der KI als Anweisung dienen, wie sie ihre Aufgabe erfüllen soll. Gib nur den fertigen Prompt als reinen Text zurück.\n\nStichpunkte:\n${currentPrompt}`;
        const payload = { contents: [{ role: "user", parts: [{ text: userPrompt }] }] };
        try {
            const response = await geminiFetch({ payload });
            if (!response.ok) throw new Error(`API-Fehler ${describeGeminiApiError(response.status, response.statusText)}`);
            const result = await response.json();
            if (result.candidates?.[0]?.content.parts?.[0]) { 
                setterFunction(result.candidates[0].content.parts[0].text); 
            } else { 
                throw new Error("Unerwartete API-Antwortstruktur."); 
            }
        } catch (err) { 
            setError(err.message); 
            console.error(err); 
        } finally { 
            setIsLoading(false); 
        }
    };

    // Notification component
    function Notification({ message, type = 'success', onClose }) {
        return (
            <div className="fixed top-4 right-4 z-50 flex items-center gap-3 bg-white dark:bg-slate-800 border border-green-200 dark:border-green-700 rounded-lg shadow-lg p-4 max-w-sm">
                <div className="shrink-0">
                    <svg className="w-6 h-6 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                </div>
                <div className="flex-1">
                    <p className="text-sm font-medium text-slate-800 dark:text-slate-200">{message}</p>
                </div>
                <button onClick={onClose} className="shrink-0 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
            </div>
        );
    }

    const countWords = (text) => {
        const trimmed = (text || '').trim();
        return trimmed ? trimmed.split(/\s+/).length : 0;
    };
    const renderPromptCount = (value) => (
        <p className="text-xs text-slate-500 mt-1" style={{ textAlign: 'right' }}>
            {countWords(value)} Wörter · {(value || '').length} Zeichen
        </p>
    );

    const selectedTemplate = templates.find(t => t.title === selectedJobTitle);
    const selectedTemplateName = selectedTemplate?.name || selectedJobTitle;

    return (
        <div className="bg-slate-100 dark:bg-slate-900 dark:text-slate-200 min-h-screen font-sans">
            <div className="container mx-auto p-4 sm:p-8 max-w-7xl">
                <header className="mb-8">
                    <button onClick={() => navigateTo('dashboard')} className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400 hover:text-green-700 mb-4">
                        <ArrowLeftIcon />
                        Zurück zum Dashboard
                    </button>
                    <h1 className="text-4xl font-bold rainbow-headline">KI-Einstellungen</h1>
                    <p className="text-slate-600 dark:text-slate-400 mt-2">Pass hier die Kriterien an, nach denen die KI Bewerber bewertet und neue Fragebögen erstellt.</p>
                </header>
                <div className="bg-white dark:bg-slate-800 dark:text-slate-200 p-6 sm:p-8 rounded-2xl shadow-lg">
                    {/* Tab Navigation */}
                    <div className="flex flex-wrap border-b border-slate-200 dark:border-slate-700 mb-8">
                        <button
                            onClick={() => setActiveTab("evaluation")}
                            className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors cursor-pointer relative inline-flex items-center gap-2 ${
                                activeTab === "evaluation"
                                    ? "border-blue-500 text-blue-600 dark:text-blue-400"
                                    : "border-transparent text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 hover:border-slate-300 dark:hover:border-slate-500"
                            }`}
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            Bewertungskriterien
                            {activeTab === "evaluation" && (
                                <div className="absolute right-0 top-1/2 transform -translate-y-1/2 w-px h-6 bg-slate-300 dark:bg-slate-600"></div>
                            )}
                        </button>
                        <button
                            onClick={() => setActiveTab("questionnaire")}
                            className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors cursor-pointer relative inline-flex items-center gap-2 ${
                                activeTab === "questionnaire"
                                    ? "border-blue-500 text-blue-600 dark:text-blue-400"
                                    : "border-transparent text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 hover:border-slate-300 dark:hover:border-slate-500"
                            }`}
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                            </svg>
                            Quiz-Erstellung
                            {activeTab === "questionnaire" && (
                                <div className="absolute right-0 top-1/2 transform -translate-y-1/2 w-px h-6 bg-slate-300 dark:bg-slate-600"></div>
                            )}
                        </button>
                        <button
                            onClick={() => setActiveTab("qualification")}
                            className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors cursor-pointer relative inline-flex items-center gap-2 ${
                                activeTab === "qualification"
                                    ? "border-blue-500 text-blue-600 dark:text-blue-400"
                                    : "border-transparent text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 hover:border-slate-300 dark:hover:border-slate-500"
                            }`}
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            Qualifikationsbewertung
                            {activeTab === "qualification" && (
                                <div className="absolute right-0 top-1/2 transform -translate-y-1/2 w-px h-6 bg-slate-300 dark:bg-slate-600"></div>
                            )}
                        </button>
                        <button
                            onClick={() => setActiveTab("scoring")}
                            className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors cursor-pointer relative inline-flex items-center gap-2 ${
                                activeTab === "scoring"
                                    ? "border-blue-500 text-blue-600 dark:text-blue-400"
                                    : "border-transparent text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 hover:border-slate-300 dark:hover:border-slate-500"
                            }`}
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-3 0-5 1.343-5 3s2 3 5 3 5 1.343 5 3-2 3-5 3m0-12c3 0 5 1.343 5 3M12 8V6m0 14v-2" />
                            </svg>
                            Punktebewertung
                        </button>
                        <button
                            onClick={() => setActiveTab("language")}
                            className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors cursor-pointer inline-flex items-center gap-2 ${
                                activeTab === "language"
                                    ? "border-blue-500 text-blue-600 dark:text-blue-400"
                                    : "border-transparent text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 hover:border-slate-300 dark:hover:border-slate-500"
                            }`}
                        >
                            <HiLanguage className="w-4 h-4" />
                            Auswertungssprache
                        </button>
                        <button
                            onClick={() => setActiveTab("model")}
                            className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors cursor-pointer inline-flex items-center gap-2 ${
                                activeTab === "model"
                                    ? "border-blue-500 text-blue-600 dark:text-blue-400"
                                    : "border-transparent text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 hover:border-slate-300 dark:hover:border-slate-500"
                            }`}
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                            </svg>
                            KI-Modell
                        </button>
                        <button
                            onClick={() => setActiveTab("colors")}
                            className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors cursor-pointer inline-flex items-center gap-2 ${
                                activeTab === "colors"
                                    ? "border-blue-500 text-blue-600 dark:text-blue-400"
                                    : "border-transparent text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 hover:border-slate-300 dark:hover:border-slate-500"
                            }`}
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
                            </svg>
                            Farben
                        </button>
                    </div>

                    {/* Tab Content */}
                    <div className="space-y-8">
                        {/* Evaluation Tab */}
                        {activeTab === "evaluation" && (
                            <div className="space-y-6">
                                {/* Job Title Dropdown */}
                                <div>
                                    <label htmlFor="job-title-select-evaluation" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                                        Job-Titel auswählen
                                    </label>
                                    <select
                                        id="job-title-select-evaluation"
                                        value={selectedJobTitle}
                                        onChange={(e) => setSelectedJobTitle(e.target.value)}
                                        className="w-full p-3 border border-slate-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                                    >
                                        {templates.length > 0 ? (
                                            templates.map((template) => (
                                                <option key={template.id} value={template.title}>
                                                    {template.name || template.title}
                                                </option>
                                            ))
                                        ) : (
                                            <option value="">Keine Templates verfügbar</option>
                                        )}
                                    </select>
                                    <p className="text-xs text-slate-500 mt-1">
                                        Wähle einen Job-Titel aus, um die spezifischen Bewertungskriterien zu bearbeiten.
                                    </p>
                                </div>

                                {/* Analysis Prompt for Selected Job */}
                                <div>
                                    <label htmlFor="analysis-prompt" className="block text-lg font-semibold text-slate-800 dark:text-slate-200 mb-2">Bewertungskriterien für "{selectedTemplateName}"</label>
                                    <p className="text-sm text-slate-500 mb-4">Definiere spezifische Bewertungskriterien für diesen Job-Titel. Diese Anweisungen werden bei der KI-Analyse von Bewerbern für diese Position verwendet.</p>
                                    <textarea 
                                        id="analysis-prompt" 
                                        value={analysisPrompt} 
                                        onChange={(e) => setAnalysisPrompt(e.target.value)} 
                                        rows="6" 
                                        className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500"
                                        placeholder="Deine spezifischen Bewertungskriterien für diesen Job-Titel..."
                                    />
                                    {renderPromptCount(analysisPrompt)}
                                    <p className="text-xs text-slate-500 mt-1">
                                        Dieser Prompt wird für die Bewerberanalyse von "{selectedTemplateName}" verwendet.
                                    </p>
                                </div>

                                <div>
                                    <label htmlFor="default-analysis-prompt" className="block text-lg font-semibold text-slate-800 dark:text-slate-200 mb-2">Standard Bewertungskriterien (Fallback-Prompt)</label>
                                    <p className="text-sm text-slate-500 mb-4">Dieser Prompt wird als Fallback verwendet, wenn kein job-spezifischer Prompt gefunden wird. Er dient als Standard für alle Positionen ohne eigene Bewertungskriterien.</p>
                                    <textarea 
                                        id="default-analysis-prompt" 
                                        value={defaultAnalysisPrompt} 
                                        onChange={(e) => setDefaultAnalysisPrompt(e.target.value)} 
                                        rows="6" 
                                        className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500"
                                        placeholder="Dein Standard-Prompt für die Bewertungskriterien..."
                                    />
                                    {renderPromptCount(defaultAnalysisPrompt)}
                                </div>

                                <div className="flex justify-end pt-4">
                                    <ModernButton 
                                      onClick={() => improveWithAI('analysis')} 
                                      disabled={isLoading} 
                                      loading={isLoading}
                                      variant="ghost" 
                                      size="md"
                                      icon={<SparklesIcon/>}
                                    >
                                      Bewertungskriterien mit KI verbessern
                                    </ModernButton>
                                </div>
                            </div>
                        )}

                        {/* Questionnaire Tab */}
                        {activeTab === "questionnaire" && (
                            <div>
                                <label htmlFor="questionnaire-prompt" className="block text-lg font-semibold text-slate-800 dark:text-slate-200 mb-2">Quiz-Erstellung (System-Prompt)</label>
                                <p className="text-sm text-slate-500 mb-4">Diese Anweisungen bestimmen, wie die KI neue Quiz erstellt. Die KI wird diese Kriterien bei der Generierung von Fragen und Titeln berücksichtigen.</p>
                                <textarea 
                                    id="questionnaire-prompt" 
                                    value={questionnairePrompt} 
                                    onChange={(e) => setQuestionnairePrompt(e.target.value)} 
                                    rows="6" 
                                    className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500"
                                    placeholder="Deine Kriterien für Quiz-Erstellung..."
                                />
                                {renderPromptCount(questionnairePrompt)}
                                
                                <div className="flex justify-end pt-4">
                                    <ModernButton 
                                      onClick={() => improveWithAI('questionnaire')} 
                                      disabled={isLoading} 
                                      loading={isLoading}
                                      variant="ghost" 
                                      size="md"
                                      icon={<SparklesIcon/>}
                                    >
                                      Quiz-Erstellung mit KI verbessern
                                    </ModernButton>
                                </div>
                            </div>
                        )}

                        {/* Qualification Tab */}
                        {activeTab === "qualification" && (
                            <div className="space-y-6">
                                {/* Job Title Dropdown */}
                                <div>
                                    <label htmlFor="job-title-select" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                                        Job-Titel auswählen
                                    </label>
                                    <select
                                        id="job-title-select"
                                        value={selectedJobTitle}
                                        onChange={(e) => setSelectedJobTitle(e.target.value)}
                                        className="w-full p-3 border border-slate-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                                    >
                                        {templates.length > 0 ? (
                                            templates.map((template) => (
                                                <option key={template.id} value={template.title}>
                                                    {template.name || template.title}
                                                </option>
                                            ))
                                        ) : (
                                            <option value="">Keine Templates verfügbar</option>
                                        )}
                                    </select>
                                    <p className="text-xs text-slate-500 mt-1">
                                        Wähle einen Job-Titel aus, um den spezifischen Qualifikationsprompt zu bearbeiten.
                                    </p>
                                </div>

                                {/* Qualification Prompt for Selected Job */}
                                <div>
                                    <label htmlFor="qualification-prompt" className="block text-lg font-semibold text-slate-800 dark:text-slate-200 mb-2">Qualifikationsbewertung für "{selectedTemplateName}"</label>
                                    <p className="text-sm text-slate-500 mb-4">Definiere, wie die KI die Rahmendaten & Qualifikationen für diesen Job-Titel bewerten soll. Diese Anweisungen bestimmen die Bewertungskriterien und Punktvergabe für die formalen Fragen.</p>
                                    <textarea 
                                        id="qualification-prompt" 
                                        value={qualificationPrompt} 
                                        onChange={(e) => setQualificationPrompt(e.target.value)} 
                                        rows="8" 
                                        className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500"
                                        placeholder="Deine Kriterien für die Qualifikationsbewertung..."
                                    />
                                    {renderPromptCount(qualificationPrompt)}
                                    <p className="text-xs text-slate-500 mt-1">
                                        Dieser Prompt wird für die Qualifikationsbewertung von Bewerbern für "{selectedTemplateName}" verwendet.
                                    </p>
                                </div>

                                {/* Default Qualification Prompt */}
                                <div>
                                    <label htmlFor="default-qualification-prompt" className="block text-lg font-semibold text-slate-800 dark:text-slate-200 mb-2">Standard Qualifikationsbewertung (Fallback-Prompt)</label>
                                    <p className="text-sm text-slate-500 mb-4">Dieser Prompt wird als Fallback verwendet, wenn kein job-spezifischer Prompt gefunden wird. Er dient als Standard für alle Positionen ohne eigene Bewertungskriterien.</p>
                                    <textarea 
                                        id="default-qualification-prompt" 
                                        value={defaultQualificationPrompt} 
                                        onChange={(e) => setDefaultQualificationPrompt(e.target.value)} 
                                        rows="8" 
                                        className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500"
                                        placeholder="Dein Standard-Prompt für die Qualifikationsbewertung..."
                                    />
                                    {renderPromptCount(defaultQualificationPrompt)}
                                    <p className="text-xs text-slate-500 mt-1">
                                        Dieser Prompt wird als Fallback verwendet, wenn kein job-spezifischer Prompt gefunden wird.
                                    </p>
                                </div>

                                <div className="flex justify-end pt-4">
                                    <ModernButton 
                                      onClick={() => improveWithAI('qualification')} 
                                      disabled={isLoading} 
                                      loading={isLoading}
                                      variant="ghost" 
                                      size="md"
                                      icon={<SparklesIcon/>}
                                    >
                                      Qualifikationsbewertung mit KI verbessern
                                    </ModernButton>
                                </div>
                            </div>
                        )}

                        {/* Scoring Tab */}
                        {activeTab === "scoring" && (
                            <div className="space-y-6">
                                {/* Recommendation badge tiers (global) */}
                                <div>
                                    <div className="flex items-start justify-between gap-4 mb-2">
                                        <label className="block text-lg font-semibold text-slate-800 dark:text-slate-200">
                                            Empfehlungs-Stufen (global)
                                        </label>
                                        <button
                                            type="button"
                                            onClick={() => setRecommendationTiers(DEFAULT_RECOMMENDATION_TIERS.map((tier) => ({ ...tier })))}
                                            className="text-xs text-blue-600 dark:text-blue-400 hover:underline whitespace-nowrap"
                                        >
                                            Standard einfügen
                                        </button>
                                    </div>
                                    <p className="text-sm text-slate-500 mb-3">
                                        Definieren Sie eigene Empfehlungs-Stufen für das Badge in den Ergebnissen. Jede Stufe gilt ab dem angegebenen Gesamtscore (in Prozent); die Stufe mit dem niedrigsten Wert fängt automatisch alles ab 0 % auf. Gilt für alle Bewerbungen.
                                    </p>
                                    <div className="space-y-2">
                                        {recommendationTiers.map((tier, index) => (
                                            <div key={index} className="flex flex-wrap items-center gap-2">
                                                <input
                                                    type="text"
                                                    value={tier.emoji}
                                                    onChange={(e) => setRecommendationTiers((prev) => prev.map((t, i) => i === index ? { ...t, emoji: e.target.value } : t))}
                                                    maxLength={4}
                                                    placeholder="✅"
                                                    title="Emoji (optional)"
                                                    className="w-14 p-2 text-center border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-green-500"
                                                />
                                                <input
                                                    type="text"
                                                    value={tier.label}
                                                    onChange={(e) => setRecommendationTiers((prev) => prev.map((t, i) => i === index ? { ...t, label: e.target.value } : t))}
                                                    placeholder={`Stufe ${index + 1}`}
                                                    title="Bezeichnung der Stufe"
                                                    className="flex-1 min-w-0 p-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-green-500"
                                                    style={{ minWidth: '10rem' }}
                                                />
                                                <span className="text-sm text-slate-600 dark:text-slate-400">ab</span>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    max="100"
                                                    value={tier.minScore}
                                                    onChange={(e) => setRecommendationTiers((prev) => prev.map((t, i) => i === index ? { ...t, minScore: e.target.value } : t))}
                                                    title="Gilt ab diesem Gesamtscore (%)"
                                                    className="w-20 p-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-green-500"
                                                />
                                                <span className="text-sm text-slate-600 dark:text-slate-400">%</span>
                                                <input
                                                    type="color"
                                                    value={/^#[0-9a-fA-F]{6}$/.test(String(tier.color || '')) ? tier.color : '#64748b'}
                                                    onChange={(e) => setRecommendationTiers((prev) => prev.map((t, i) => i === index ? { ...t, color: e.target.value } : t))}
                                                    title="Farbe des Badges"
                                                    className="w-10 h-10 p-1 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 cursor-pointer"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => setRecommendationTiers((prev) => prev.filter((_, i) => i !== index))}
                                                    disabled={recommendationTiers.length <= 1}
                                                    className="p-2 text-slate-400 hover:text-red-600 dark:hover:text-red-400 disabled:opacity-30 disabled:hover:text-slate-400 rounded-lg transition-colors"
                                                    title="Stufe entfernen"
                                                >
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => setRecommendationTiers((prev) => [...prev, { label: '', minScore: '', color: '#64748b', emoji: '' }])}
                                        className="mt-3 px-3 py-2 text-sm font-semibold bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 rounded-md hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors inline-flex items-center gap-1"
                                    >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                                        Stufe hinzufügen
                                    </button>
                                    {(() => {
                                        const tiers = normalizeRecommendationTiers(recommendationTiers);
                                        const parts = [...tiers].reverse().map((tier, index, list) => {
                                            const next = list[index + 1];
                                            const range = next ? `${tier.minScore}–${next.minScore - 1} %` : `ab ${tier.minScore} %`;
                                            return `${tier.emoji ? `${tier.emoji} ` : ''}${tier.label} ${range}`;
                                        });
                                        return (
                                            <p className="text-xs text-slate-500 mt-2">
                                                Ergibt: {parts.join(' · ')}
                                            </p>
                                        );
                                    })()}
                                </div>

                                {/* Global Point-Scoring Framework Prompt */}
                                <div className="pt-8 border-t border-slate-200 dark:border-slate-700">
                                    <div className="flex items-start justify-between gap-4 mb-2">
                                        <label htmlFor="point-scoring-prompt" className="block text-lg font-semibold text-slate-800 dark:text-slate-200">
                                            Punktevergabe-Framework (global)
                                        </label>
                                        <button
                                            type="button"
                                            onClick={() => setPointScoringPrompt(DEFAULT_POINT_SCORING_PROMPT)}
                                            className="text-xs text-blue-600 dark:text-blue-400 hover:underline whitespace-nowrap"
                                        >
                                            Standard einfügen
                                        </button>
                                    </div>
                                    <p className="text-sm text-slate-500 mb-3">
                                        Dieser Prompt steuert, wie die KI Punkte auf Fragen und Antwortoptionen verteilt (z. B. die Regel, dass keine zwei Optionen den gleichen Wert haben dürfen). Gilt für alle Job-Titel. Lass das Feld leer, um den Standard aus dem Code zu verwenden.
                                    </p>
                                    <div className="mb-3 rounded-lg bg-slate-50 dark:bg-slate-900/40 border border-slate-200 dark:border-slate-700 p-3">
                                        <p className="text-xs font-medium text-slate-700 dark:text-slate-300 mb-2">Verfügbare Platzhalter:</p>
                                        <ul className="text-xs text-slate-600 dark:text-slate-400 space-y-1">
                                            {POINT_SCORING_PROMPT_PLACEHOLDERS.map((p) => (
                                                <li key={p.token}>
                                                    <code className="font-mono text-slate-800 dark:text-slate-200">{p.token}</code>
                                                    {' — '}{p.description}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                    <textarea
                                        id="point-scoring-prompt"
                                        value={pointScoringPrompt}
                                        onChange={(e) => setPointScoringPrompt(e.target.value)}
                                        rows="14"
                                        className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 font-mono text-sm"
                                        placeholder={DEFAULT_POINT_SCORING_PROMPT}
                                    />
                                    {renderPromptCount(pointScoringPrompt)}
                                    <p className="text-xs text-slate-500 mt-1">
                                        Wird zentral für die KI-Punktevergabe im Fragebogen-Editor verwendet.
                                    </p>
                                </div>

                                {/* Job Title Dropdown */}
                                <div className="pt-8 border-t border-slate-200 dark:border-slate-700">
                                    <label htmlFor="job-title-select-scoring" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                                        Job-Titel auswählen
                                    </label>
                                    <select
                                        id="job-title-select-scoring"
                                        value={selectedJobTitle}
                                        onChange={(e) => setSelectedJobTitle(e.target.value)}
                                        className="w-full p-3 border border-slate-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                                    >
                                        {templates.length > 0 ? (
                                            templates.map((template) => (
                                                <option key={template.id} value={template.title}>
                                                    {template.name || template.title}
                                                </option>
                                            ))
                                        ) : (
                                            <option value="">Keine Templates verfügbar</option>
                                        )}
                                    </select>
                                    <p className="text-xs text-slate-500 mt-1">
                                        Wähle einen Job-Titel aus, um die spezifische Scoring-Erweiterung zu bearbeiten.
                                    </p>
                                </div>

                                {/* Scoring Prompt for Selected Job */}
                                <div>
                                    <label htmlFor="scoring-prompt" className="block text-lg font-semibold text-slate-800 dark:text-slate-200 mb-2">
                                        Scoring-Erweiterung für "{selectedTemplateName}"
                                    </label>
                                    <p className="text-sm text-slate-500 mb-4">
                                        Die Bewertungskriterien sind die Basis (Analyse-Prompt). Diese Scoring-Erweiterung ergänzt nur die Regeln für die Punktevergabe.
                                    </p>
                                    <textarea
                                        id="scoring-prompt"
                                        value={scoringPrompt}
                                        onChange={(e) => setScoringPrompt(e.target.value)}
                                        rows="8"
                                        className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500"
                                        placeholder="Optionale Zusatzregeln für die Punktevergabe..."
                                    />
                                    {renderPromptCount(scoringPrompt)}
                                    <p className="text-xs text-slate-500 mt-1">
                                        Leer lassen ist erlaubt. Dann wird nur mit den Bewertungskriterien bewertet.
                                    </p>
                                </div>

                                <div className="flex justify-end pt-4">
                                    <ModernButton
                                      onClick={() => improveWithAI('scoring')}
                                      disabled={isLoading}
                                      loading={isLoading}
                                      variant="ghost"
                                      size="md"
                                      icon={<SparklesIcon/>}
                                    >
                                      Scoring-Erweiterung mit KI verbessern
                                    </ModernButton>
                                </div>
                            </div>
                        )}

                        {activeTab === "language" && (
                            <div className="space-y-6">
                                <div>
                                    <label htmlFor="evaluation-language-instruction" className="block text-lg font-semibold text-slate-800 dark:text-slate-200 mb-2">
                                        Auswertung immer in Deutsch
                                    </label>
                                    <p className="text-sm text-slate-500 mb-4">
                                        Diese Anweisung wird direkt in den Prompt für die KI-Auswertung übernommen. Du kannst den Text jederzeit anpassen, ohne Code zu ändern.
                                    </p>
                                    <textarea
                                        id="evaluation-language-instruction"
                                        value={evaluationLanguageInstruction}
                                        onChange={(e) => setEvaluationLanguageInstruction(e.target.value)}
                                        rows="8"
                                        className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500"
                                        placeholder="z.B. Antworte ausschließlich auf Deutsch (de-DE). Alle Freitextfelder müssen auf Deutsch sein."
                                    />
                                    {renderPromptCount(evaluationLanguageInstruction)}
                                </div>
                            </div>
                        )}

                        {activeTab === "model" && (
                            <div className="space-y-6">
                                <div>
                                    <label htmlFor="gemini-model-select" className="block text-lg font-semibold text-slate-800 dark:text-slate-200 mb-2">
                                        Gemini-Modell für alle KI-Aufrufe
                                    </label>
                                    <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
                                        Wirkt sich auf Bewertungspipeline, Fragenerstellung, Punktevergabe und Prompt-Verbesserung aus. Die Auswahl wird lokal im Browser gespeichert.
                                    </p>
                                    <select
                                        id="gemini-model-select"
                                        value={geminiModel}
                                        onChange={(e) => {
                                            const next = e.target.value;
                                            setGeminiModel(next);
                                            setActiveGeminiModel(next);
                                            showNotification(`KI-Modell auf ${next} gesetzt`);
                                        }}
                                        className="w-full p-3 border border-slate-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                                    >
                                        {SUPPORTED_GEMINI_MODELS.map((m) => (
                                            <option key={m.id} value={m.id}>
                                                {m.label} — {m.id}
                                            </option>
                                        ))}
                                    </select>
                                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-2">
                                        {SUPPORTED_GEMINI_MODELS.find((m) => m.id === geminiModel)?.hint || ''}
                                    </p>
                                </div>
                                <div className="pt-6 border-t border-slate-200 dark:border-slate-700">
                                    <GeminiAttemptLog />
                                </div>
                                <div className="p-4 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 text-sm text-amber-800 dark:text-amber-200">
                                    Tipp: Bei unerwarteten <code>404 – Nicht gefunden</code>-Fehlern liegt es meist daran, dass der Modellname auf der Gemini-API (noch) nicht verfügbar ist. Wechsel dann zurück auf <code>gemini-3.5-flash</code> oder <code>gemini-2.5-flash</code>.
                                </div>
                            </div>
                        )}

                        {/* User Colors Tab */}
                        {activeTab === "colors" && (
                            <UserColorSettings />
                        )}

                        {activeTab !== "colors" && (
                            <div className="flex flex-wrap gap-4 justify-end pt-4">
                                <ModernButton
                                  onClick={handleSave}
                                  variant="success"
                                  size="lg"
                                  icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" /></svg>}
                                >
                                  Einstellungen speichern
                                </ModernButton>
                            </div>
                        )}
                        {error && <p className="text-red-600 text-sm mt-4 text-right">{error}</p>}
                    </div>
                </div>
            </div>
            {notification && (
                <Notification 
                    message={notification.message} 
                    type={notification.type} 
                    onClose={hideNotification} 
                />
            )}
        </div>
    );
}
